import lookupDataService from '@/services/data/LookupDataService';
import auth from '@/services/UserAuthService';
import { Privilege } from '@/types/model/permissions';
import { CAResponseEntry } from '@/types/model/inspection-modal';

/**
 * PermissionService — evaluates user privileges against CA business rules.
 *
 * Reads privileges from UserPreferences (via LookupDataService) and the
 * current user ID (via UserAuthService). All methods are synchronous and
 * read cached data — they are safe to call in render functions.
 *
 * Import the singleton: `import permissionService from '@/services/PermissionService';`
 */
class PermissionService {
  // ── Role helpers ──────────────────────────────────────────────────────────

  private getPrivileges(): string[] {
    const prefs = lookupDataService.getUserPreferences();
    if (!prefs?.Privileges) return [];
    return prefs.Privileges.split(',').map(p => p.trim());
  }

  hasRole(role: Privilege): boolean {
    return this.getPrivileges().includes(role);
  }

  isInAnyRole(...roles: Privilege[]): boolean {
    const privileges = this.getPrivileges();
    return roles.some(r => privileges.includes(r));
  }

  private getCurrentUserId(): number | null {
    const details = auth.getUserDetails();
    if (details?.userId === undefined || details.userId === null) return null;
    const id = Number(details.userId);
    return isNaN(id) ? null : id;
  }

  // ── Role-based access ────────────────────────────────────────────────────

  private getRoles(): string[] {
    const details = auth.getUserDetails();
    if (!details?.roles) return [];
    return details.roles;
  }

  isEnterpriseAdmin(): boolean {
    return this.getRoles().includes('EnterpriseAdmin');
  }

  isModuleAdmin(): boolean {
    return this.getRoles().includes('ModuleAdministrator');
  }

  isSuperAdmin(): boolean {
    return this.getRoles().includes('SuperAdmin');
  }

  // ── Planning — Group permissions ─────────────────────────────────────────

  /** Add a new Group — requires EditInspectionPlans or EditGroups */
  canAddGroup(): boolean {
    return this.isInAnyRole('EditInspectionPlans', 'EditGroups');
  }

  /** Edit an existing Group — requires EditInspectionPlans or EditGroups */
  canEditGroup(): boolean {
    return this.isInAnyRole('EditInspectionPlans', 'EditGroups');
  }

  // ── Planning — Line Item permissions ──────────────────────────────────────

  /** Add a new Line Item — requires EditInspectionPlans or EditLineItems */
  canAddLineItem(): boolean {
    return this.isInAnyRole('EditInspectionPlans', 'EditLineItems');
  }

  /** Edit an existing Line Item — requires EditInspectionPlans or EditLineItems */
  canEditLineItem(): boolean {
    return this.isInAnyRole('EditInspectionPlans', 'EditLineItems');
  }

  /** Activate or deactivate a Line Item — requires ActivateInspectionPlans */
  canActivateLineItem(): boolean {
    return this.hasRole('ActivateInspectionPlans');
  }

  // ── Planning — Inspection Plan permissions ────────────────────────────────

  /**
   * Edit Inspection Plan content.
   * Requires EditInspectionPlans; plan must be unlocked.
   */
  canEditInspectionPlan(isLocked: boolean): boolean {
    return !isLocked && this.hasRole('EditInspectionPlans');
  }

  /**
   * Activate or deactivate an Inspection Plan.
   * Requires ActivateInspectionPlans; plan must be unlocked.
   */
  canActivateInspectionPlan(isLocked: boolean): boolean {
    return !isLocked && this.hasRole('ActivateInspectionPlans');
  }

  /**
   * Lock an Inspection Plan.
   * Requires LockInspectionPlans; parent line item must be active.
   */
  canLockInspectionPlan(isLineItemActive: boolean): boolean {
    return isLineItemActive && this.hasRole('LockInspectionPlans');
  }

  /**
   * Unlock an Inspection Plan.
   * Requires LockInspectionPlans; parent line item must be active.
   */
  canUnlockInspectionPlan(isLineItemActive: boolean): boolean {
    return isLineItemActive && this.hasRole('LockInspectionPlans');
  }

  /**
   * Reset an Inspection Plan (clears all checklist items, frequencies, config).
   * Requires EditInspectionPlans; plan must be unlocked.
   */
  canResetInspectionPlan(isLocked: boolean): boolean {
    return !isLocked && this.hasRole('EditInspectionPlans');
  }

  /**
   * Add an Inspection Result to a plan.
   * Requires EditOwnInspectionResults; plan must be active, have at least one
   * checklist item, and have a frequency configured.
   */
  canAddInspectionResult(isActive: boolean, hasChecklist: boolean, hasFrequency: boolean): boolean {
    return isActive && hasChecklist && hasFrequency && this.hasRole('EditOwnInspectionResults');
  }

  /**
   * Mark an Inspection Result as complete (set IsInProgress: false).
   * Requires EditOwnInspectionResults or EditInspectionResults.
   */
  canMarkAsComplete(): boolean {
    return this.isInAnyRole('EditOwnInspectionResults', 'EditInspectionResults');
  }

  /**
   * Approve or disapprove an Inspection Result.
   * Requires ApproveInspectionResults.
   */
  canApproveInspection(): boolean {
    return this.hasRole('ApproveInspectionResults');
  }

  /**
   * Generate a customer feedback link.
   * Requires EnterpriseAdmin or Module Administrator role; plan must be active.
   */
  canGenerateCustomerFeedbackLink(isActive: boolean): boolean {
    return isActive && (this.isEnterpriseAdmin() || this.isModuleAdmin());
  }

  // ── CA Issue permissions ──────────────────────────────────────────────────

  /** Add a new CA — requires EditOwnCAs */
  canAddCA(): boolean {
    return this.hasRole('EditOwnCAs');
  }

  /**
   * Edit a CA issue.
   * - Status must be 0 (Issue In Progress).
   * - Own CA → EditOwnCAs; someone else's CA → EditCAs.
   */
  canEditCA(statusId: number, issuedUserId: number): boolean {
    if (statusId !== 0) return false;
    const currentUserId = this.getCurrentUserId();
    if (currentUserId !== null && currentUserId === Number(issuedUserId)) {
      return this.hasRole('EditOwnCAs');
    }
    return this.hasRole('EditCAs');
  }

  /**
   * Delete a CA.
   * - DeleteReopenCAs at any status, OR
   * - Status 0 and the user already has edit permission.
   */
  canDeleteCA(statusId: number, issuedUserId: number): boolean {
    if (this.hasRole('DeleteReopenCAs')) return true;
    return this.canEditCA(statusId, issuedUserId);
  }

  // ── Response permissions ──────────────────────────────────────────────────

  /**
   * Add a response to a CA.
   * - Requires EditOwnCAs.
   * - CA status must be 1, 2, 3, or 5.
   */
  canAddResponse(caStatusId: number): boolean {
    return this.hasRole('EditOwnCAs') && [1, 2, 3, 5].includes(caStatusId);
  }

  /**
   * Edit an existing response entry.
   * - Response status must be 1 (Response Pending) or 5 (Response in Progress).
   * - No subsequent entry with an acceptance status (2, 3, 4, 6) may exist.
   * - Own CA → EditOwnCAs; someone else's CA → EditCAs.
   */
  canEditResponse(
    responseStatusId: number,
    caIssuedUserId: number,
    responses: CAResponseEntry[],
    responseIndex: number,
  ): boolean {
    if (![1, 5].includes(responseStatusId)) return false;
    const hasLaterAcceptance = responses
      .slice(responseIndex + 1)
      .some(r => [2, 3, 4, 6].includes(r.statusId));
    if (hasLaterAcceptance) return false;
    const currentUserId = this.getCurrentUserId();
    if (currentUserId !== null && currentUserId === Number(caIssuedUserId)) {
      return this.hasRole('EditOwnCAs');
    }
    return this.hasRole('EditCAs');
  }

  // ── Acceptance permissions ────────────────────────────────────────────────

  /**
   * Add an acceptance entry.
   * - AcceptCAs when CA status is 1, 2, 3, or 5.
   * - DeleteReopenCAs when CA status is 4 or 6 (re-opening a closed CA).
   */
  canAddAcceptance(caStatusId: number): boolean {
    if (this.hasRole('AcceptCAs') && [1, 2, 3, 5].includes(caStatusId)) return true;
    if (this.hasRole('DeleteReopenCAs') && [4, 6].includes(caStatusId)) return true;
    return false;
  }
}

const permissionService = new PermissionService();
export default permissionService;
